/**
 * AF-4082: dado PARCIAL coletado de um item INTERRUPTED (hoje só INOVAMIND).
 *
 * O item passa pelo motor de regras como parcial (partial + pending_fields): o motor nunca o
 * aprova e devolve PENDING ou REJECTED. O analysis_status de topo nunca é exibido como decisão
 * do item.
 */
export const NOT_COLLECTED_LABEL = 'Não coletado';

export interface PartialCollectionItem {
    field: string;
    label: string;
}

export interface PartialCollectedItem extends PartialCollectionItem {
    value: unknown;
}

export interface PartialCollection {
    collected: PartialCollectedItem[];
    pending: PartialCollectionItem[];
}

const HIDDEN_FIELDS = new Set([
    'analysisstatus',
    'analysis_status',
    'partial',
    'pending_fields',
    'collected_fields',
]);

const isHiddenField = (field: string): boolean => HIDDEN_FIELDS.has(field.trim().toLowerCase());

const toLabel = (field: string, headers?: Record<string, string>): string => headers?.[field] ?? field;

const toStringList = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

export const isPartialReport = (report: any): boolean =>
    (report?.status || '').toUpperCase() === 'INTERRUPTED' && report?.partial === true;

export const buildPartialCollection = (
    report: any,
    headers?: Record<string, string>
): PartialCollection | null => {
    if (!isPartialReport(report)) return null;

    const pending = toStringList(report.pending_fields)
        .filter((field) => !isHiddenField(field))
        .map((field) => ({ field, label: toLabel(field, headers) }));

    const collected = toStringList(report.collected_fields)
        .filter((field) => !isHiddenField(field))
        .map((field) => ({ field, label: toLabel(field, headers), value: report[field] }));

    return { collected, pending };
};

const summarizeCollection = (value: object): string => {
    if (Array.isArray(value)) return value.length === 0 ? '-' : `[${value.length} itens]`;
    const keys = Object.keys(value);
    return keys.length === 0 ? '-' : `[${keys.length} itens]`;
};

export const formatCollectedValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
    if (typeof value === 'object') return summarizeCollection(value as object);
    return String(value);
};
